/**
 * Formats a phone number to the standard format: +7XXXXXXXXXX (11 digits)
 *
 * @param phoneNumber - The phone number to format
 * @returns Formatted phone number in +7XXXXXXXXXX format, or empty string if invalid
 *
 * @example
 * formatPhoneNumber("74956217420") // "+74956217420"
 * formatPhoneNumber("+7 903 798 81 98") // "+79037988198"
 * formatPhoneNumber("8-495-730-52-60") // "+74957305260"
 * formatPhoneNumber("8 495 7 800 500") // "+74957800500"
 */
export const formatPhoneNumber = (phoneNumber?: string): string => {
  if (!phoneNumber) {
    return '';
  }

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Handle different cases
  if (digits.length === 11) {
    // If starts with 8, replace with 7
    if (digits.startsWith('8')) {
      return `+7${digits.slice(1)}`;
    }
    // If starts with 7, add +
    if (digits.startsWith('7')) {
      return `+${digits}`;
    }
  }

  if (digits.length === 10) {
    // Assume it's a Russian number without country code
    return `+7${digits}`;
  }

  // If already has correct length and starts with 7, just add +
  if (digits.length === 11 && digits.startsWith('7')) {
    return `+${digits}`;
  }

  // If none of the above cases match, return original or empty
  // You can adjust this behavior based on your needs
  return digits.length === 11 && digits.startsWith('7') ? `+${digits}` : '';
};

/**
 * Formats a phone number from +79037988198 format to X XXX XXX XX XX format
 *
 * @param phoneNumber - The phone number in +7XXXXXXXXXX format
 * @returns Formatted phone number in X XXX XXX XX XX format, or empty string if invalid
 *
 * @example
 * formatPhoneNumberDisplay("+79037988198") // "7 903 798 81 98"
 * formatPhoneNumberDisplay("+74956217420") // "7 495 621 74 20"
 */
export const formatPhoneNumberDisplay = (phoneNumber?: string): string => {
  if (!phoneNumber) {
    return '';
  }

  // Remove all non-digit characters
  const digits = phoneNumber.replace(/\D/g, '');

  // Check if the number has the correct length (11 digits) and starts with 7
  if (digits.length === 11 && digits.startsWith('7')) {
    // Format as: X XXX XXX XX XX
    return `${digits[0]} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`;
  }

  // If the format is not correct, return empty string
  return phoneNumber;
};
